import { api } from '@/api/client';
import type { DeliveryEstimate, Order, PlaceOrderPayload } from '@/types/order';

export const orderKeys = {
  orders: ['orders'] as const,
  order: (orderNumber: string) => ['orders', orderNumber] as const,
  estimate: (country: string, method?: string, discountCode?: string) =>
    ['cart', 'estimate', country, method ?? '', discountCode ?? ''] as const,
};

/** Delivery options and tax for a destination — §13/§33/§55. */
export function fetchDeliveryEstimate(
  country: string,
  method?: string,
  discountCode?: string,
): Promise<DeliveryEstimate> {
  /*
   * The method is part of the request because the total depends on it —
   * quoting against one method and displaying another's price is how the
   * summary came to contradict itself.
   */
  return api.get<DeliveryEstimate>('/cart/estimate', {
    country,
    ...(method ? { method } : {}),
    ...(discountCode ? { discount_code: discountCode } : {}),
  });
}

/**
 * Places the order.
 *
 * The payload carries no prices — the server computes every figure from the
 * database, so nothing here can influence what is charged (§29).
 */
export function placeOrder(payload: PlaceOrderPayload): Promise<Order> {
  return api.post<Order>('/checkout/orders', payload);
}

export function fetchOrders(): Promise<Order[]> {
  return api.get<Order[]>('/orders');
}

export function fetchOrder(orderNumber: string): Promise<Order> {
  return api.get<Order>(`/orders/${orderNumber}`);
}

/**
 * Read an order using the checkout token — BUILD-PLAN §24, §29.
 *
 * The guest counterpart of `fetchOrder`. Needed because a customer returning
 * from a payment gateway arrives as a fresh document: router state is gone,
 * they have no account, and without this the confirmation page has nothing to
 * show and never asks whether the payment succeeded.
 */
export function fetchOrderByToken(orderNumber: string, checkoutToken: string): Promise<Order> {
  return api.get<Order>(`/checkout/orders/${orderNumber}`, undefined, {
    headers: { 'X-Checkout-Token': checkoutToken },
  });
}

export function cancelOrder(orderNumber: string): Promise<Order> {
  return api.post<Order>(`/orders/${orderNumber}/cancel`);
}
